import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Alert,
  Chip
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { usePlayer } from '../../context/PlayerContext';
import './AddBuyInDialog.scss';

const AddBuyInDialog = ({ open, onClose, playerId }) => {
  const { addBuyIn, getPlayerById } = usePlayer();
  const [amount, setAmount] = useState('200');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  
  const player = getPlayerById(playerId);
  const MIN_BUYIN = 100;
  const MAX_BUYIN = 500;

  const validateAmount = (value) => {
    const buyInAmount = parseFloat(value);
    
    if (!value || isNaN(buyInAmount) || buyInAmount <= 0) {
      return 'Please enter a valid amount';
    }

    if (buyInAmount < MIN_BUYIN) {
      return `Minimum buy-in amount is $${MIN_BUYIN}`;
    }

    if (buyInAmount > MAX_BUYIN) {
      return `Maximum buy-in amount is $${MAX_BUYIN}`;
    }

    return '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const validationError = validateAmount(amount);
    if (validationError) {
      setError(validationError);
      return;
    }

    const buyInAmount = parseFloat(amount);
    addBuyIn(playerId, buyInAmount, note.trim());
    setAmount('200');
    setNote('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setAmount('200');
    setNote('');
    setError('');
    onClose();
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setAmount(value);
      
      // Clear error when user starts typing
      if (error) {
        setError('');
      }
      
      // Show real-time validation for better UX
      if (value && !isNaN(parseFloat(value))) {
        const validationError = validateAmount(value);
        if (validationError) {
          setError(validationError);
        }
      }
    }
  };

  const handlePresetAmount = (presetValue) => {
    setAmount(presetValue.toString());
    // Clear any existing errors
    if (error) {
      setError('');
    }
  };

  const isValidAmount = () => {
    const buyInAmount = parseFloat(amount);
    return amount && !isNaN(buyInAmount) && buyInAmount >= MIN_BUYIN && buyInAmount <= MAX_BUYIN;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="add-buyin-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <AttachMoneyIcon className="title-icon" />
            <Box>
              <Typography variant="h5">
                Add Buy-in
              </Typography>
              {player && (
                <Typography variant="body2" color="text.secondary">
                  for {player.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="form-fields">
            <Alert severity="info" className="validation-info">
              Buy-in amount must be between ${MIN_BUYIN} and ${MAX_BUYIN}
            </Alert>
            
            <TextField
              autoFocus
              fullWidth
              label="Amount"
              variant="outlined"
              value={amount}
              onChange={handleAmountChange}
              error={!!error}
              helperText={error || `Enter amount between $${MIN_BUYIN} - $${MAX_BUYIN}`}
              placeholder="200.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    $
                  </InputAdornment>
                ),
              }}
              className="amount-input"
            />
            
            {/* Preset Amount Options */}
            <Box className="preset-amounts-section">
              <Typography variant="body2" color="text.secondary" className="preset-label">
                Quick Select:
              </Typography>
              <Box className="preset-amounts">
                {[100, 200, 300, 400, 500].map((presetValue) => (
                  <Chip
                    key={presetValue}
                    label={`$${presetValue}`}
                    onClick={() => handlePresetAmount(presetValue)}
                    variant={parseInt(amount) === presetValue ? "filled" : "outlined"}
                    color={parseInt(amount) === presetValue ? "primary" : "default"}
                    className="preset-chip"
                    clickable
                  />
                ))}
              </Box>
            </Box>
            
            <TextField
              fullWidth
              label="Note (Optional)"
              variant="outlined"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note..."
              multiline
              rows={2}
              className="note-input"
            />
          </Box>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={!isValidAmount()}
          >
            Add Buy-in
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddBuyInDialog;